import { mt } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { ChevronDown } from 'lucide-react';
import { type ReactNode, useContext } from 'react';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { SplitButtonLayout } from 'v5/design-system/SplitButtonLayout';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { startMutation } from 'v5/platform/react/startMutation';
import { ResourcePickerCloseContext } from './ResourcePickerCommit';

/** One way to act on the pick, as its host defines it. */
interface ResourcePickerAction {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  /** The host's rule: whether it works for this many picked (one editor opens for one person). */
  isEnabledFor: (count: number) => boolean;
  /** Why it is off, on hover; it stays in the list, greyed. */
  unavailableHint?: string;
  /**
   * A save returns its promise: the dialog closes once it resolves and keeps the pick and the
   * error when it rejects. An action that returns nothing (it opens an editor) handles itself.
   */
  onSelect?: () => void | Promise<void>;
  /** A submenu of further actions instead (one per status). */
  items?: ResourcePickerAction[];
}

/**
 * The picker's actions at the end of its footer: the first as the button, the others in its
 * menu, each on or off by the host's rule for how many are picked. With nobody picked all are off.
 */
export function ResourcePickerActions({
  count,
  actions,
  moreLabel,
  failure
}: {
  count: number;
  actions: ResourcePickerAction[];
  /** The menu button's name. */
  moreLabel: string;
  /** What a failed save says. */
  failure: string;
}) {
  const close = useContext(ResourcePickerCloseContext);
  const running = promiseState.useMutation(
    async (save: () => Promise<void>) => {
      await save();
      close?.();
    }
  );
  const busy = mt.isInProgress(running.state);
  const enabled = (action: ResourcePickerAction) =>
    count > 0 && !busy && action.isEnabledFor(count);
  const select = (action: ResourcePickerAction) => {
    const result = action.onSelect?.();
    if (result) startMutation(running, () => result);
  };
  const [primary, ...rest] = actions;

  const item = (action: ResourcePickerAction): ReactNode => {
    if (action.items) {
      return (
        <DropdownMenuSub key={action.key}>
          <DropdownMenuSubTrigger disabled={!enabled(action)}>
            {action.icon}
            {action.label}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {action.items.map(item)}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }
    const menuItem = (
      <DropdownMenuItem
        key={action.key}
        disabled={!enabled(action)}
        onSelect={() => select(action)}
      >
        {action.icon}
        {action.label}
        {count > 0 && !action.isEnabledFor(count) && action.unavailableHint && (
          <span className="sr-only">{action.unavailableHint}</span>
        )}
      </DropdownMenuItem>
    );
    // a disabled item takes no pointer: the hint hangs on what is around it
    return count > 0 &&
      !action.isEnabledFor(count) &&
      action.unavailableHint ? (
      <SimpleTooltip key={action.key} title={action.unavailableHint}>
        <div>{menuItem}</div>
      </SimpleTooltip>
    ) : (
      menuItem
    );
  };

  return (
    <>
      {mt.isInError(running.state) && (
        <p role="alert" className="text-sm text-fg-danger">
          {failure}
        </p>
      )}
      {primary && (
        <SplitButtonLayout
          leftSlot={
            // the spinner covers any action's save; nothing runs twice meanwhile
            <AsyncButton
              type="button"
              variant="primaryAlt"
              size="sm"
              mutation={running.state}
              disabled={count === 0 || !primary.isEnabledFor(count)}
              onClick={() => select(primary)}
            >
              {primary.label}
            </AsyncButton>
          }
          rightSlot={
            rest.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="primaryAlt"
                    size="sm"
                    layout="icon"
                    aria-label={moreLabel}
                    disabled={count === 0 || busy}
                  >
                    <ChevronDown />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {rest.map(item)}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null
          }
        />
      )}
    </>
  );
}
